/** @jsx isolateComponent */

import keycode from 'keycode';
import { useState, useEffect } from 'react';
import type { Interpolation } from '@emotion/css';
import {
  FloatingBox,
  IconButton,
  Menu,
  Colors,
  useClickOutside,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Link } from 'components/Link/Link';

import { CollapsibleMenu } from './CollapsibleMenu';
import type { NavItemType, NavSize, OnChooseItem } from './types';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';

type Props = {
  /**
   * Whether to render a selected state.
   */
  shouldIndicateSelected: boolean;
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: NavSize;
};

const NavDropdownButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: Colors.grey000,
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey700,
    },
    hover: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
    focus: {
      backgroundColor: Colors.grey100,
      outlineColor: 'transparent',
      color: Colors.blue500,
    },
    selected: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
  },
};

const Styles = labelStyles('NavDropdown', {
  root: {
    position: 'relative',
  },

  summary: {
    // Hides default icon on details element
    '&::-webkit-details-marker': {
      display: 'none',
    },
    '&::before': {
      display: 'none',
    },
  },

  menu: {
    padding: 10,
    minWidth: 250,
  },
});

/**
 * This is what renders the dropdown/menu when you click the three ... button when the user is on a smaller screen.
 * This component exists in ZDS, but, doesn't have the capability of displaying submenus. That said, we had to add that functionality
 * on top of the existing code.
 */
export const NavDropdown = (props: Props) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [revealedMenuItemIndex, setRevealedMenuItemIndex] = useState<
    null | number
  >(null);

  useEffect(() => {
    if (!isOpen && revealedMenuItemIndex !== null) {
      setRevealedMenuItemIndex(null);
    }
  }, [isOpen]);

  const menuItems = props.items.map((item, index) => {
    /**
     * Does the item contain a submenu or not?
     */
    if (!item.submenu.length) {
      return (
        <li key={index}>
          <StyledLink
            onClick={(event) => {
              setIsOpen(false);
              props.onChooseItem(item, event);
              router.push(item.href);
            }}
            href={item.href}
          >
            {item.text}
          </StyledLink>
        </li>
      );
    }
    const isMenuItemOpen = revealedMenuItemIndex === index;
    return (
      <li key={index}>
        <CollapsibleMenu
          title={item.text}
          submenu={item.submenu}
          onClick={() =>
            isMenuItemOpen
              ? setRevealedMenuItemIndex(null)
              : setRevealedMenuItemIndex(index)
          }
          isOpen={isMenuItemOpen}
          onChildClick={(event) => {
            setIsOpen(false);
            props.onChooseItem(item, event);
          }}
        />
      </li>
    );
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const detailsHandlers = {
    onClick: (event: React.MouseEvent) => event.preventDefault(),
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.keyCode === keycode('escape')) {
        setIsOpen(false);
      }
    },
  };
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const summaryHandlers = {
    onClick: () => setIsOpen(!isOpen),
  };
  return (
    <details
      {...detailsHandlers}
      css={Styles.root as Interpolation}
      open={isOpen}
      ref={setClickOutsideNode}
    >
      <summary
        {...summaryHandlers}
        aria-expanded={isOpen}
        aria-label="More items…"
        css={Styles.summary as Interpolation}
        role="button"
        tabIndex={0}
      >
        <IconButton
          color="custom"
          colorTheme={NavDropdownButtonColorTheme}
          icon="navMoreHoriz"
          isSelected={
            props.shouldIndicateSelected &&
            props.items.some((item) => item.href === props.selectedItemHref)
          }
          label="More items…"
          size={
            {
              medium: 'xsmall' as 'xsmall',
              large: 'small' as 'small',
            }[props.size]
          }
          tooltipPosition="west"
        />
      </summary>
      {isOpen && (
        <FloatingBox align="right">
          <div css={Styles.menu as Interpolation}>
            <Menu ariaLabel="">{menuItems}</Menu>
          </div>
        </FloatingBox>
      )}
    </details>
  );
};

const StyledLink = styled(Link)`
  display: block;
  border-radius: 10px;
  color: ${Colors.grey700};
  line-height: 20px;
  font-size: 14px;
  padding: 10px;

  &:hover,
  &:focus {
    color: ${Colors.blue500};
    background-color: ${Colors.blue100};
  }
`;
